/**
 * Owner: Masa Bayu
 * File: qx-writer-page-number-style.ts
 * Date:Time: 2026-10-16 07:42
 * Description: Page numbers · section model preferred · legacy page scope.
 */

import {
  computeSectionPagePaints,
  currentPageSection,
  rootUsesPageSections,
  seedAcademicPageSections,
  SECTIONS_JSON_ATTR,
  updatePageSectionProps,
  type SectionPageFormat,
} from './qx-writer-page-sections';

export const PAGE_NUMBER_STYLE_ATTR = 'data-qxw-pagenum-style';
/** Per-sheet override · absent → document default (or academic matter). */
export const PAGE_NUMBER_LOCAL_ATTR = 'data-qxw-pagenum-local';

export const PAGE_NUMBER_STYLES = [
  'arabic',
  'roman-lower',
  'roman-upper',
  'academic',
  'none',
] as const;

export type PageNumberStyle = (typeof PAGE_NUMBER_STYLES)[number];

/** Where a chosen style is written. */
export type PageNumberApplyScope = 'page' | 'following' | 'document';

export type PageNumberStyleOption = {
  readonly id: PageNumberStyle;
  readonly label: string;
  readonly hint: string;
};

export const PAGE_NUMBER_STYLE_OPTIONS: readonly PageNumberStyleOption[] = Object.freeze([
  { id: 'arabic', label: '1, 2, 3…', hint: 'Angka Arab' },
  { id: 'roman-lower', label: 'i, ii, iii, iv, v…', hint: 'Roman kecil' },
  { id: 'roman-upper', label: 'I, II, III, IV…', hint: 'Roman besar' },
  {
    id: 'academic',
    label: 'Academic (auto Front/Main)',
    hint: 'Front Matter Roman · Main/Back Arab (seluruh dokumen)',
  },
  { id: 'none', label: 'None', hint: 'Sembunyi nombor' },
]);

export const PAGE_NUMBER_SCOPE_OPTIONS: readonly {
  readonly id: PageNumberApplyScope;
  readonly label: string;
}[] = Object.freeze([
  { id: 'page', label: 'This page only' },
  { id: 'following', label: 'This page and following' },
  { id: 'document', label: 'Whole document' },
]);

const ROMAN_MAP: readonly [number, string][] = [
  [1000, 'm'],
  [900, 'cm'],
  [500, 'd'],
  [400, 'cd'],
  [100, 'c'],
  [90, 'xc'],
  [50, 'l'],
  [40, 'xl'],
  [10, 'x'],
  [9, 'ix'],
  [5, 'v'],
  [4, 'iv'],
  [1, 'i'],
];

const SPINE_PAGE = 'qxw-spine-page';
const SPINE_PAGENUM = 'qxw-spine-page__pagenum';
const SPINE_SUBLABEL = 'qxw-spine-page__sublabel';

function isPageSubLabel(label: string): boolean {
  return /^Page\s+\d+$/i.test(label.trim());
}

export function parsePageNumberStyle(value: unknown): PageNumberStyle {
  if (typeof value !== 'string') return 'arabic';
  const trimmed = value.trim().toLowerCase();
  return (PAGE_NUMBER_STYLES as readonly string[]).includes(trimmed)
    ? (trimmed as PageNumberStyle)
    : 'arabic';
}

export function parsePageNumberApplyScope(value: unknown): PageNumberApplyScope {
  if (value === 'page' || value === 'following' || value === 'document') return value;
  return 'following';
}

/** Convert 1-based integer to Roman (iv, v, …). */
export function toRomanNumeral(n: number, upper: boolean): string {
  if (!Number.isFinite(n) || n < 1) return upper ? 'I' : 'i';
  let rest = Math.min(3999, Math.floor(n));
  let out = '';
  for (const [value, glyph] of ROMAN_MAP) {
    while (rest >= value) {
      out += glyph;
      rest -= value;
    }
  }
  return upper ? out.toUpperCase() : out;
}

export function pageNumberStyleFromHtml(html: string): PageNumberStyle | null {
  const match = html.match(/data-qxw-pagenum-style=["']([^"']+)["']/i);
  return match ? parsePageNumberStyle(match[1]) : null;
}

export function readPageNumberStyle(root: HTMLElement | null | undefined): PageNumberStyle {
  if (!root) return 'arabic';
  const fromRoot = root.getAttribute(PAGE_NUMBER_STYLE_ATTR);
  if (fromRoot) return parsePageNumberStyle(fromRoot);
  return 'arabic';
}

export function writePageNumberStyle(root: HTMLElement, style: PageNumberStyle): void {
  root.setAttribute(PAGE_NUMBER_STYLE_ATTR, style);
}

function inferMatter(kind: string): 'cover' | 'front' | 'main' | 'back' {
  if (kind === 'cover') return 'cover';
  if (
    kind === 'title-page'
    || kind === 'copyright-page'
    || kind === 'table-of-contents'
    || kind === 'chapter-opening'
  ) {
    return 'front';
  }
  if (kind === 'back-matter' || kind === 'author-bio') return 'back';
  return 'main';
}

export function sheetMatterZone(sheet: HTMLElement): 'cover' | 'front' | 'main' | 'back' {
  const raw = sheet.getAttribute('data-qxw-spine-matter');
  if (raw === 'cover' || raw === 'front' || raw === 'main' || raw === 'back') return raw;
  return inferMatter(sheet.getAttribute('data-qxw-spine-kind') || 'section');
}

export function listSpineSheets(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll(`.${SPINE_PAGE}`)).filter(
    (el): el is HTMLElement => el instanceof HTMLElement,
  );
}

export function focusedSpineSheet(root: HTMLElement | null | undefined): HTMLElement | null {
  if (!root) return null;
  const sel = root.ownerDocument.getSelection();
  const anchor = sel?.anchorNode;
  const el = anchor instanceof Element ? anchor : anchor?.parentElement ?? null;
  const sheet = el?.closest(`.${SPINE_PAGE}`);
  if (sheet instanceof HTMLElement) return sheet;
  return listSpineSheets(root)[0] ?? null;
}

/** Style used to paint this sheet (local override · academic matter · document). */
export function effectiveSheetPageNumberStyle(
  sheet: HTMLElement,
  docStyle: PageNumberStyle,
): PageNumberStyle {
  const local = sheet.getAttribute(PAGE_NUMBER_LOCAL_ATTR);
  if (local) return parsePageNumberStyle(local);
  const doc = parsePageNumberStyle(docStyle);
  if (doc !== 'academic') return doc;
  const matter = sheetMatterZone(sheet);
  if (matter === 'cover') return 'none';
  if (matter === 'front') return 'roman-lower';
  return 'arabic';
}

export type FormattedPageNumber = {
  readonly ordinal: number;
  readonly text: string;
  readonly visible: boolean;
  readonly style: PageNumberStyle;
};

function displayForStyle(style: PageNumberStyle, n: number): FormattedPageNumber {
  if (style === 'none' || style === 'academic') {
    return { ordinal: n, text: '', visible: false, style };
  }
  if (style === 'arabic') {
    return { ordinal: n, text: String(n), visible: true, style };
  }
  if (style === 'roman-upper') {
    return { ordinal: n, text: toRomanNumeral(n, true), visible: true, style };
  }
  return { ordinal: n, text: toRomanNumeral(n, false), visible: true, style };
}

/**
 * Format footer for sheet · counters restart when effective style changes
 * (e.g. i…vii then 1, 2, 3…).
 */
export function formatSpinePageNumber(
  docStyle: PageNumberStyle,
  sheetIndex: number,
  sheets: readonly HTMLElement[],
): FormattedPageNumber {
  const sheet = sheets[sheetIndex];
  if (!sheet) return displayForStyle('arabic', sheetIndex + 1);
  const style = effectiveSheetPageNumberStyle(sheet, docStyle);
  if (style === 'none') {
    return { ordinal: sheetIndex + 1, text: '', visible: false, style };
  }
  let runStart = sheetIndex;
  while (runStart > 0) {
    const prev = sheets[runStart - 1];
    if (!prev) break;
    if (effectiveSheetPageNumberStyle(prev, docStyle) !== style) break;
    runStart -= 1;
  }
  const n = sheetIndex - runStart + 1;
  return displayForStyle(style, n);
}

function paintSheetPageMark(
  root: HTMLElement,
  sheet: HTMLElement,
  index: number,
  text: string,
  visible: boolean,
  effective: string,
  align: 'center' | 'right' = 'center',
  italic = false,
): void {
  sheet.setAttribute('data-qxw-doc-page', String(index + 1));
  sheet.setAttribute('data-qxw-doc-page-label', text);
  sheet.setAttribute('data-qxw-pagenum-effective', effective);
  for (const h2 of Array.from(sheet.querySelectorAll(':scope > h2'))) {
    if (!isPageSubLabel(h2.textContent ?? '')) continue;
    h2.classList.add(SPINE_SUBLABEL);
    h2.setAttribute('aria-hidden', 'true');
  }
  const existingMark = sheet.querySelector(`:scope > .${SPINE_PAGENUM}`);
  const markEl: HTMLElement =
    existingMark instanceof HTMLElement
      ? existingMark
      : root.ownerDocument.createElement('div');
  if (!(existingMark instanceof HTMLElement)) {
    markEl.setAttribute('contenteditable', 'false');
    markEl.setAttribute('aria-hidden', 'true');
    sheet.append(markEl);
  }
  markEl.className = SPINE_PAGENUM;
  markEl.classList.toggle(`${SPINE_PAGENUM}--right`, align === 'right');
  markEl.classList.toggle(`${SPINE_PAGENUM}--italic`, italic);
  markEl.setAttribute('data-qxw-doc-page', String(index + 1));
  markEl.setAttribute('data-qxw-doc-page-label', text);
  markEl.setAttribute('data-qxw-pagenum-align', align);
  markEl.setAttribute('data-qxw-pagenum-italic', italic ? '1' : '0');
  markEl.textContent = text;
  markEl.hidden = !visible;
}

function sectionFormatAsStyle(format: SectionPageFormat): PageNumberStyle {
  return format === 'none' ? 'none' : format;
}

/**
 * Assign style to one page, from this page onward, or whole document · then repaint.
 * `academic` seeds the official Word section model (Front roman · Body arabic).
 */
export function assignSpinePageNumberStyle(
  root: HTMLElement,
  style: PageNumberStyle,
  scope: PageNumberApplyScope,
  focusSheet?: HTMLElement | null,
): PageNumberStyle {
  const resolved = parsePageNumberStyle(style);
  const sheets = listSpineSheets(root);
  const focus = focusSheet && sheets.includes(focusSheet)
    ? focusSheet
    : focusedSpineSheet(root);
  const focusIndex = focus ? sheets.indexOf(focus) : 0;

  if (resolved === 'academic') {
    writePageNumberStyle(root, 'academic');
    for (const sheet of sheets) sheet.removeAttribute(PAGE_NUMBER_LOCAL_ATTR);
    seedAcademicPageSections(root);
    applySpineDocumentPageNumbers(root);
    return resolved;
  }

  // Once sections exist, style edits the focused section format (Word model).
  if (rootUsesPageSections(root)) {
    const section = currentPageSection(root, focus);
    if (
      resolved === 'arabic'
      || resolved === 'roman-lower'
      || resolved === 'roman-upper'
      || resolved === 'none'
    ) {
      updatePageSectionProps(root, section.id, { format: resolved });
    }
    applySpineDocumentPageNumbers(root);
    return resolved;
  }

  if (scope === 'document') {
    writePageNumberStyle(root, resolved);
    for (const sheet of sheets) sheet.setAttribute(PAGE_NUMBER_LOCAL_ATTR, resolved);
  } else {
    const start = Math.max(0, focusIndex);
    const end = scope === 'page' ? start : sheets.length - 1;
    for (let i = start; i <= end; i += 1) {
      const sheet = sheets[i];
      if (sheet) sheet.setAttribute(PAGE_NUMBER_LOCAL_ATTR, resolved);
    }
    if (!root.getAttribute(PAGE_NUMBER_STYLE_ATTR)) {
      writePageNumberStyle(root, resolved);
    }
  }
  applySpineDocumentPageNumbers(root);
  return resolved;
}

/**
 * Document page numbers · bottom centre.
 * When `data-qxw-sections` is present → Word section engine; else legacy locals.
 * Presentation only · skipped by draft tip sync.
 */
export function applySpineDocumentPageNumbers(
  root: HTMLElement,
  style: PageNumberStyle = readPageNumberStyle(root),
): number {
  const sheets = listSpineSheets(root);
  if (rootUsesPageSections(root) || root.hasAttribute(SECTIONS_JSON_ATTR)) {
    const paints = computeSectionPagePaints(root);
    writePageNumberStyle(root, parsePageNumberStyle(style));
    sheets.forEach((sheet, index) => {
      const paint = paints[index];
      const text = paint?.text ?? '';
      const visible = paint?.visible ?? false;
      const effective = paint
        ? sectionFormatAsStyle(
          (readPageSectionsFormat(root, paint.sectionId)),
        )
        : 'arabic';
      paintSheetPageMark(
        root,
        sheet,
        index,
        text,
        visible,
        effective,
        paint?.align ?? 'center',
        paint?.italic ?? false,
      );
      if (paint) sheet.setAttribute('data-qxw-section-id', String(paint.sectionId));
    });
    return sheets.length;
  }

  const docStyle = parsePageNumberStyle(style);
  writePageNumberStyle(root, docStyle);
  sheets.forEach((sheet, index) => {
    const formatted = formatSpinePageNumber(docStyle, index, sheets);
    paintSheetPageMark(
      root,
      sheet,
      index,
      formatted.text,
      formatted.visible,
      formatted.style,
    );
  });
  return sheets.length;
}

function readPageSectionsFormat(root: HTMLElement, sectionId: number): SectionPageFormat {
  try {
    const raw = root.getAttribute(SECTIONS_JSON_ATTR);
    if (!raw) return 'arabic';
    const parsed = JSON.parse(raw) as Array<{ id?: number; format?: string }>;
    const hit = parsed.find((item) => item.id === sectionId);
    return hit?.format === 'roman-lower'
      || hit?.format === 'roman-upper'
      || hit?.format === 'none'
      || hit?.format === 'arabic'
      ? hit.format
      : 'arabic';
  } catch {
    return 'arabic';
  }
}

/** @deprecated use assignSpinePageNumberStyle · document scope */
export function setSpinePageNumberStyle(
  root: HTMLElement,
  style: PageNumberStyle,
): PageNumberStyle {
  return assignSpinePageNumberStyle(root, style, 'document');
}

/** Plain text for word count · omit footer numbers + hidden Page N labels. */
export function plainTextWithoutSpineChrome(root: HTMLElement): string {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll(`.${SPINE_PAGENUM}, .${SPINE_SUBLABEL}`).forEach((node) => node.remove());
  return clone.innerText ?? '';
}

/** 1-based sheet index under caret · else 1. */
export function currentSpineDocumentPage(root: HTMLElement | null | undefined): number {
  if (!root) return 1;
  const sheets = listSpineSheets(root);
  if (sheets.length === 0) return 1;
  const sheet = focusedSpineSheet(root);
  if (!sheet) return 1;
  const idx = sheets.indexOf(sheet);
  return idx >= 0 ? idx + 1 : 1;
}

/** Footer label for status bar (iv, 3, …) under caret. */
export function currentSpineDocumentPageLabel(
  root: HTMLElement | null | undefined,
): string {
  if (!root) return '1';
  const sheets = listSpineSheets(root);
  if (sheets.length === 0) return '1';
  const active = focusedSpineSheet(root) ?? sheets[0]!;
  const label = active.getAttribute('data-qxw-doc-page-label')?.trim();
  if (label) return label;
  const idx = sheets.indexOf(active);
  const formatted = formatSpinePageNumber(
    readPageNumberStyle(root),
    Math.max(0, idx),
    sheets,
  );
  return formatted.text || String(idx + 1);
}

/** Effective style of the focused sheet · for Format select. */
export function currentSpinePageNumberStyle(
  root: HTMLElement | null | undefined,
): PageNumberStyle {
  if (!root) return 'arabic';
  if (rootUsesPageSections(root)) {
    const section = currentPageSection(root, focusedSpineSheet(root));
    return sectionFormatAsStyle(section.format);
  }
  const doc = readPageNumberStyle(root);
  const sheet = focusedSpineSheet(root);
  if (!sheet) return doc;
  return effectiveSheetPageNumberStyle(sheet, doc);
}
